import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { LifeBuoy, Loader2, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supportTicketsApi, type SupportTicketResponse } from "@/lib/api";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/support")({
  component: SupportPage,
});

const statusColors: Record<string, string> = {
  open: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  in_progress: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  closed: "bg-muted text-muted-foreground",
};

const priorityColors: Record<string, string> = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

function SupportPage() {
  const [tickets, setTickets] = useState<SupportTicketResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await supportTicketsApi.list({ status: statusFilter || undefined, search: search || undefined });
      setTickets(data);
    } catch {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchTickets();
  };

  const handleStatusChange = async (id: string, status: string) => {
    setUpdatingId(id);
    try {
      await supportTicketsApi.updateStatus(id, { status });
      toast.success("Ticket updated");
      fetchTickets();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const activeCount = tickets.filter((t) => t.status === "open" || t.status === "in_progress").length;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Support</h1>
          <p className="text-sm text-muted-foreground">Resolve customer issues and keep stays on track.</p>
        </div>
        <Badge variant="secondary" className="gap-1.5">
          <LifeBuoy className="size-3" /> {activeCount} active
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search tickets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
          </div>
          <Button type="submit" size="sm" variant="secondary">Search</Button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-20 text-center shadow-soft">
          <LifeBuoy className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">No support tickets found</p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            {search || statusFilter ? "Try adjusting your filters." : "Customer support requests will appear here."}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Ref</th>
                <th className="px-4 py-3 text-left font-medium">Guest</th>
                <th className="px-4 py-3 text-left font-medium">Subject</th>
                <th className="px-4 py-3 text-left font-medium">Category</th>
                <th className="px-4 py-3 text-left font-medium">Priority</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{t.ticketRef}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.guestName}</div>
                    <div className="text-xs text-muted-foreground">{t.guestEmail}</div>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate" title={t.message}>{t.subject}</td>
                  <td className="px-4 py-3 capitalize">{t.category}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs capitalize ${priorityColors[t.priority] || ""}`}>{t.priority}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs capitalize ${statusColors[t.status] || ""}`}>{t.status.replace("_", " ")}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {updatingId === t.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <select
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                        value={t.status}
                        onChange={(e) => handleStatusChange(t.id, e.target.value)}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
